import { Table, Tag, Button, Space, Tooltip } from '@/shared/ui/primitives';
import { EditOutlined, DeleteOutlined, EyeOutlined, LinkOutlined } from '@/shared/ui/icons';
import { formatDateTime } from '@/shared/utils/formatters';
import { useTranslation } from 'react-i18next';
import type { ColumnsType, TableProps } from '@/shared/ui/subpath-types';
import { publicRoomAdUrl } from '@/shared/utils/publicLinks';

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default',
  PUBLISHED: 'success',
  ARCHIVED: 'warning',
  REJECTED: 'error',
};

interface Props {
  data: any[];
  loading: boolean;
  sortField: string;
  sortDir: string;
  onSort: (field: string) => void;
  onView: (id: number) => void;
  onEdit: (id: number) => void;
  onDelete: (item: any) => void;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  selectedIds?: number[];
  onSelectionChange?: (ids: number[]) => void;
}

export default function RoomAdTable({ data, loading, onSort, onView, onEdit, onDelete, page, pageSize, total, onPageChange, selectedIds, onSelectionChange }: Props) {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith('ar');

  const columns: ColumnsType<any> = [
    { title: t('common.id'), dataIndex: 'id', sorter: true, width: 80 },
    {
      title: t('roomAds.title'), dataIndex: 'title', sorter: true, width: 200, ellipsis: true,
      render: (v: string) => v || '—',
    },
    {
      title: t('roomAds.status'), dataIndex: 'status', sorter: true, width: 110,
      render: (v: string) => <Tag color={STATUS_COLORS[v] || 'default'}>{v}</Tag>,
    },
    {
      title: t('roomAds.advertiserType'), dataIndex: 'advertiserType', sorter: true,
      render: (v: string) => v || '—',
    },
    {
      title: t('roomAds.propertyType'), dataIndex: 'propertyType', sorter: true,
      render: (v: string) => v || '—',
    },
    {
      title: t('roomAds.user'), dataIndex: 'userName',
      render: (_: any, record: any) => record.userName ? `${record.userName} (#${record.userId})` : '—',
    },
    {
      title: t('roomAds.city'), dataIndex: 'cityNameEn',
      render: (_: string, record: any) => localizedGeoName(record, 'city', isArabic),
    },
    {
      title: t('roomAds.featured'), dataIndex: 'isFeatured', sorter: true, width: 100,
      render: (v: boolean) => <Tag color={v ? 'purple' : 'default'}>{v ? t('common.yes') : t('common.no')}</Tag>,
    },
    {
      title: t('roomAds.source'), dataIndex: 'importSource', width: 110,
      render: (v: string) => v ? <Tag color="geekblue">{v}</Tag> : <Tag>{t('roomAds.sourceManual')}</Tag>,
    },
    {
      title: t('common.createdAt'), dataIndex: 'createdAt', sorter: true,
      render: (v: string) => formatDateTime(v),
    },
    {
      title: t('common.actions'), width: 160,
      render: (_: any, record: any) => {
        const publicUrl = publicRoomAdUrl(record.id);
        return (
          <Space>
            <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => onView(record.id)} data-testid={`roomad-view-${record.id}`} />
            <span data-testid={`roomad-public-link-${record.id}`}>
              <Tooltip title={t('common.viewOnSite', { defaultValue: 'View on site' })}>
                <Button
                  type="text"
                  size="small"
                  icon={<LinkOutlined />}
                  aria-label={t('common.viewOnSite', { defaultValue: 'View on site' })}
                  onClick={() => window.open(publicUrl, '_blank', 'noopener,noreferrer')}
                />
              </Tooltip>
            </span>
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit(record.id)} data-testid={`roomad-edit-${record.id}`} />
            <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(record)} data-testid={`roomad-delete-${record.id}`} />
          </Space>
        );
      },
    },
  ];

  const handleChange: TableProps['onChange'] = (_pagination, _filters, sorter: any, extra: any) => {
    // Only re-sort on an actual sort action — otherwise paginating after a sort
    // resets back to page 0 (handleSort calls setPage(0)).
    if (extra?.action === 'sort' && sorter?.field) onSort(sorter.field);
  };

  const rowSelection = onSelectionChange
    ? {
        selectedRowKeys: selectedIds ?? [],
        onChange: (keys: React.Key[]) => onSelectionChange(keys.map((k) => Number(k))),
      }
    : undefined;

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={data}
      loading={loading}
      onChange={handleChange}
      sortDirections={['ascend', 'descend']}
      showSorterTooltip={false}
      rowSelection={rowSelection}
      scroll={{ x: 1280 }}
      pagination={{
        current: page + 1,
        pageSize,
        total,
        showTotal: (total) => `${total} items`,
        showSizeChanger: false,
        onChange: (p) => onPageChange(p - 1),
      }}
    />
  );
}

function localizedGeoName(record: any, type: 'city' | 'district', isArabic: boolean) {
  const ar = record?.[`${type}NameAr`];
  const en = record?.[`${type}NameEn`];
  return (isArabic ? ar || en : en || ar) || '—';
}
